import React, { useState, useEffect, useRef } from "react"
import { Box, Button, useTheme } from "@chakra-ui/react"
import Dropzone from "react-dropzone"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faUndo } from "@fortawesome/pro-light-svg-icons"
import { dataURLtoBlob } from "../../utilities/imageUtils"
import { drawLine, clearLine } from "../../services/CanvasDraw"
import styles from "./css/ImageUploadDraw.module.css"
import Card from "../../common/components/Card"
import useColors from "../../common/hooks/useColors"

const ImageUploadDraw = (props) => {
  const { blueColor } = useColors()
  const [isImageUploaded, setIsImageUploaded] = useState(false)
  const [isLineDrawn, setIsLineDrawn] = useState(false)
  const [imageRotation, setImageRotation] = useState(0)
  const imageToUpload = useRef(null)
  const canvasRef = useRef(null)

  const drawImage = () => {
    const {
      canvasId,
      isEditing,
      isLineDisabled,
      onImageCreated,
      setLineDrawn,
    } = props

    const canvas = canvasRef.current
    const maxSize = 1070
    let width = imageToUpload.current.naturalWidth
    let height = imageToUpload.current.naturalHeight
    if (width > height) {
      if (width > maxSize) {
        height *= maxSize / width
        width = maxSize
      }
    } else if (height > maxSize) {
      width *= maxSize / height
      height = maxSize
    }

    if (imageRotation % 2 === 1) {
      const oldW = width
      const oldH = height
      width = oldH
      height = oldW
    }
    canvas.width = width
    canvas.height = height
    canvas.style.width = width + "px"
    canvas.style.height = height + "px"
    canvas.style.marginRight = Number((-1 * width) / 2) + "px"
    canvas.style.marginBottom = Number((-1 * height) / 2) + "px"
    const ctx = canvas.getContext("2d")
    ctx.save()
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.translate(width / 2, height / 2)
    ctx.rotate((-90 * imageRotation * Math.PI) / 180)
    const rWidth = imageRotation % 2 === 0 ? width : height
    const rHeight = imageRotation % 2 === 0 ? height : width
    ctx.drawImage(
      imageToUpload.current,
      -(rWidth / 2),
      -(rHeight / 2),
      rWidth,
      rHeight,
    )
    ctx.restore()
    if (isLineDisabled) {
      const dataUrl = canvas.toDataURL("image/jpeg")
      const resizedImage = dataURLtoBlob(dataUrl)
      onImageCreated("picture", resizedImage, isEditing)
    } else {
      drawLine(canvas, "#03A9F4", () => {
        setLineDrawn(true)
        const dataUrl = canvas.toDataURL("image/jpeg")
        const resizedImage = dataURLtoBlob(dataUrl)
        onImageCreated("picture", resizedImage, isEditing)
      })
    }
  }

  useEffect(() => {
    if (isImageUploaded) {
      drawImage()
    }
  }, [imageRotation])

  const {
    canvasId,
    dropTitle,
    isLineDisabled,
    setImageUploaded,
    setLineDrawn,
  } = props

  return (
    <Card>
      <Box
        display={isImageUploaded ? "flex" : "none"}
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
      >
        <div className={styles["canvas-container"]}>
          <canvas
            className={styles["image-canvas"]}
            ref={canvasRef}
            id={canvasId || "imageDrawCanvas"}
          />
        </div>
        {isImageUploaded ? (
          <Box mt={5} justifyContent="space-between" display="flex">
            <Button
              mr={3}
              variant="secondary"
              onClick={() => {
                const canvas = canvasRef.current
                const ctx = canvas.getContext("2d")
                ctx.clearRect(0, 0, canvas.width, canvas.height)
                setLineDrawn(false)
                setImageUploaded(false)
                setIsImageUploaded(false)
              }}
            >
              Remove Image
            </Button>
            <Button
              mr={3}
              variant="secondary"
              onClick={() => {
                setImageRotation((prevRotation) => prevRotation + 1)
                setLineDrawn(false)
              }}
            >
              <FontAwesomeIcon color={blueColor} icon={faUndo} />
            </Button>
            {isLineDrawn && !isLineDisabled ? (
              <Button
                variant="secondary"
                onClick={() => {
                  clearLine()
                  setLineDrawn(false)
                }}
              >
                Clear Line
              </Button>
            ) : null}
          </Box>
        ) : null}
      </Box>
      {isImageUploaded ? null : (
        <Dropzone
          accept={{
            "image/*": [".jpeg", ".png"],
          }}
          className={styles["image-drop"]}
          onDrop={(acceptedFiles) => {
            setIsImageUploaded(true)
            const reader = new FileReader()
            reader.onload = (event) => {
              imageToUpload.current = new Image()
              imageToUpload.current.onload = () => {
                drawImage()
              }
              imageToUpload.current.src = event.target.result
            }
            reader.readAsDataURL(acceptedFiles[0])
          }}
        >
          {({ getRootProps, getInputProps }) => (
            <div className={styles["image-drop"]} {...getRootProps()}>
              <input {...getInputProps()} />
              <h6>{dropTitle}</h6>
            </div>
          )}
        </Dropzone>
      )}
    </Card>
  )
}

export default ImageUploadDraw
